import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import CyberNav from '../CyberNav/CyberNav';

// Import shared utilities and components
import { detectLanguageDirection, wrapWithLanguage } from '../../../utils/languageUtils';
import { extractMetadata, sanitizeHtml, ArticleMetadata } from '../../../utils/markdownUtils';
import ErrorBoundary from '../../Shared/ErrorBoundary';
import CodeBlock from '../../Shared/CodeBlock';

// CSS imports order is important!
import './Markdown-Global.css';    // 1. Base styles
import './CyberPages.css';        // 2. Cyber specific layout
import './CyberArticles.css';

// ייבוא של הקומפוננטה החיצונית של ArticlesList
import ArticlesList from './ArticlesList';

// Using the shared CodeBlock component instead of local implementation

// Using shared language utilities instead of local implementation

// Using shared markdown utilities and components instead of local implementation

const CyberArticles = () => {
  const { fileName } = useParams();
  const [content, setContent] = useState('');
  const [metadata, setMetadata] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBox, setShowBox] = useState(true);

  useEffect(() => {
    const fetchMarkdownFile = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/md/CyberArticles/${fileName}`);
        if (!response.ok) throw new Error('File not found');
        const text = await response.text();
        const { content: parsedContent, metadata: parsedMetadata } = extractMetadata(text);
        setContent(parsedContent);
        setMetadata(parsedMetadata);
        setError(null);
      } catch (error) {
        setError(error.message);
        setContent('');
        setMetadata({});
      } finally {
        setLoading(false);
      }
    };

    if (fileName) {
      fetchMarkdownFile();
    }
  }, [fileName]);

  return (
    <div className="cyber-wrapper">
      {!fileName && showBox && (
        <div className="rtl-box">
          <button className="close-btn" onClick={() => setShowBox(false)}>×</button>
          <p>ברוכים הבאים למאמרי הסייבר שלנו! כאן תמצאו מאמרים מעמיקים בנושאי אבטחת מידע.</p>
          <p>המאמרים מכסים מגוון רחב של נושאים, מחדשות אבטחה ועד לניתוחי מקרים ומחקרים.</p>
        </div>
      )}

      <div className="cyber-page-layout">
        <CyberNav />
        {fileName ? (
          <div className="markdown-content" lang={detectLanguageDirection(content)}>
            {loading && <p>Loading...</p>}
            {error ? (
              <p className="error">{error}</p>
            ) : (
              <>
                <ArticleMetadata metadata={metadata} />
                <ErrorBoundary>
                  <ReactMarkdown
                    children={sanitizeHtml(content)}
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({node, children, ...props}) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h1 {...props}>{wrapWithLanguage(children, lang, true)}</h1>;
                      },
                      h2: ({node, children, ...props}) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h2 {...props}>{wrapWithLanguage(children, lang, true)}</h2>;
                      },
                      h3: ({node, children, ...props}) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h3 {...props}>{wrapWithLanguage(children, lang, true)}</h3>;
                      },
                      p: ({node, children, ...props}) => {
                        const lang = detectLanguageDirection(String(children));
                        return <p {...props} lang={lang}>{wrapWithLanguage(children, lang, false)}</p>;
                      },
                      code: ({node, inline, className, children, ...props}) => {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <CodeBlock className={className} {...props}>
                            {children}
                          </CodeBlock>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  />
                </ErrorBoundary>
              </>
            )}
          </div>
        ) : (
          <ArticlesList 
            folderName="CyberArticles"
            basePath="cyber/hacking/articles"
            defaultCategory="Cyber Articles"
          />
        )}
      </div>
    </div>
  );
};

export default CyberArticles;